/**
 * SMS-side wrapper around `useSetActiveOrganization` from
 * `@aegix/auth-organization` that invalidates the React Query cache
 * whenever the active org changes.
 *
 * The lib's setter is intentionally Query-agnostic — it persists,
 * fires the cross-tab heartbeat, and that's it. SMS needs the extra
 * step: every server response is scoped by the `X-Vendor-Organization`
 * header (see `lib/http-client.ts:vendorOrgAccessor`), so cached
 * responses from the previous org would render stale data until each
 * query's natural staleTime ticked over. Invalidating on org change
 * forces every active observer to refetch under the new scope.
 *
 * Use this hook in place of `useAuth().setActiveOrganization` — the call
 * signature matches (accepts `ActiveOrganization | null`).
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSetActiveOrganization as useLibSetActiveOrganization } from '@aegix/auth-organization';
import type { ActiveOrganization } from '../lib/sms-auth';

export function useSetActiveOrganization(): (
  org: ActiveOrganization | null,
) => void {
  const setLib = useLibSetActiveOrganization();
  const queryClient = useQueryClient();

  return useCallback(
    (org: ActiveOrganization | null) => {
      setLib(org);
      // Masquerade-sensitive responses refetch under the
      // new vendor-org header.
      queryClient.invalidateQueries();
    },
    [setLib, queryClient],
  );
}
